import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CheckCircleOutlined,
  ErrorOutlineOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  ImageOutlined,
  PhotoSizeSelectLargeOutlined,
  SaveAltOutlined,
  WarningAmberOutlined,
  BrandingWatermarkOutlined,
} from "@mui/icons-material";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import WatermarkArt from "../assets/watermark.png";
import {
  copyName,
  isSupportedImage,
  looksWatermarked,
  loadWatermark,
  watermarkImage,
} from "../watermark";
import { radius, tokens } from "../theme/theme";

/**
 * Batch watermarking, entirely on the realtor's own machine.
 *
 * Nothing here uploads: photos are read from the disk, composited on a canvas,
 * and written straight back. S3 and the database are not involved, and the
 * photos never leave the computer.
 *
 * Photos are picked individually, because a listing's shots usually sit in a
 * folder of thousands and nobody wants to tick their way through it.
 *
 * Both modes then write through a DIRECTORY handle, asked for once with
 * `startIn` pointing at the photos themselves — so the dialog opens already in
 * the right folder and one click covers the whole batch.
 *
 * Writing through each picked file's own handle looks simpler and cannot work.
 * `createWritable()` needs write permission; requesting it needs transient user
 * activation AND CONSUMES IT, so in a loop the first photo is granted and every
 * one after it throws. A directory is granted once and every file inside it
 * inherits that grant — it is the only way to write a batch.
 *
 * The corollary is unavoidable and worth stating: OVERWRITING ONLY WORKS FOR
 * PHOTOS IN A FOLDER CHROME WILL GRANT. Its blocklist refuses the home folder
 * and the Desktop, Documents and Downloads folders themselves (children are
 * fine), so photos sitting loose in Downloads cannot be overwritten in place by
 * any page, ever. checkPresence() below turns that into a sentence the realtor
 * can act on instead of a raw NotFoundError.
 *
 * Browsers without the File System Access API (Firefox, Safari) fall back to a
 * plain file input and downloads, which is the best they can do.
 */

const HAS_FS_ACCESS =
  typeof window !== "undefined" &&
  typeof window.showOpenFilePicker === "function" &&
  typeof window.showDirectoryPicker === "function";

const PICKER_TYPES = [
  {
    description: "Фото",
    accept: {
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "image/webp": [".webp"],
    },
  },
];

const PREVIEW_MAX_SIDE = 900;

/**
 * Chrome will not hand a page certain folders, whatever the user clicks, and
 * says only "contains system files".
 *
 * The rule that actually bites here is not about system files at all. Chrome's
 * blocklist marks the home folder and the Desktop, Documents and DOWNLOADS
 * folders as blocked *themselves* while allowing everything inside them — so
 * a photos folder that IS the browser's download directory (F:\downloads, say)
 * is refused, and a subfolder of it is fine. Saying that is far more use than
 * repeating Chrome's wording.
 */
function blockedFolderHelp() {
  return (
    "Chrome не відкриває сайтам саму теку завантажень, «Робочий стіл», " +
    "«Документи» чи домашню теку — але вкладені теки дозволяє. " +
    "Виберіть підпапку (наприклад F:\\downloads\\фото) або іншу теку."
  );
}

export default function WatermarkPage() {
  const [watermark, setWatermark] = useState(null);
  const [watermarkError, setWatermarkError] = useState("");

  // Where copies go. Set by picking a folder outright, or — when photos were
  // picked one by one — by the single folder prompt copy mode asks for.
  const [dirHandle, setDirHandle] = useState(null);
  const [dirName, setDirName] = useState("");
  const [entries, setEntries] = useState([]);
  const [mode, setMode] = useState("copy");

  const [activeName, setActiveName] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  const [running, setRunning] = useState(false);
  const [doneCount, setDoneCount] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [notice, setNotice] = useState(null);
  // What the last run produced, for the folder button.
  const [saved, setSaved] = useState(null);
  const [savedOpen, setSavedOpen] = useState(false);

  const fileInput = useRef(null);
  const previewUrl = useRef("");

  useEffect(function () {
    loadWatermark(WatermarkArt).then(setWatermark, function (err) {
      setWatermarkError(err.message);
    });
  }, []);

  // Object URLs are not garbage collected on their own.
  useEffect(function () {
    return function () {
      if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    };
  }, []);

  const selected = entries.filter(function (entry) {
    return entry.selected;
  });

  const renderPreview = useCallback(
    function (entry) {
      if (!entry || !watermark) return;
      setActiveName(entry.name);
      setPreviewBusy(true);
      Promise.resolve(entry.handle ? entry.handle.getFile() : entry.file)
        .then(function (file) {
          return watermarkImage(file, watermark, {
            name: entry.name,
            maxSide: PREVIEW_MAX_SIDE,
          });
        })
        .then(function (result) {
          if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
          previewUrl.current = URL.createObjectURL(result.blob);
          setPreview({
            url: previewUrl.current,
            width: result.sourceWidth,
            height: result.sourceHeight,
          });
          setPreviewBusy(false);
        })
        .catch(function (err) {
          setPreview(null);
          setPreviewBusy(false);
          setNotice({ severity: "warning", text: entry.name + ": " + err.message });
        });
    },
    [watermark]
  );

  const pickPhotos = async function () {
    setNotice(null);
    try {
      const handles = await window.showOpenFilePicker({
        multiple: true,
        types: PICKER_TYPES,
        excludeAcceptAllOption: false,
      });
      const found = handles
        .filter(function (handle) {
          return isSupportedImage(handle.name);
        })
        .map(function (handle) {
          const already = looksWatermarked(handle.name);
          return {
            name: handle.name,
            handle: handle,
            selected: !already,
            already: already,
            status: "",
          };
        });

      // Picked files carry no folder of their own, so a destination chosen for
      // an earlier batch must not silently catch this one.
      setDirHandle(null);
      setDirName("");
      setEntries(found);
      setDoneCount(0);
      setPreview(null);
      setActiveName("");
      if (found.length > 0) {
        const first = found.filter(function (entry) {
          return entry.selected;
        })[0];
        renderPreview(first || found[0]);
      }
    } catch (err) {
      if (err && err.name !== "AbortError") {
        setNotice({ severity: "error", text: err.message });
      }
    }
  };

  const pickFolder = async function () {
    setNotice(null);
    try {
      // readwrite up front: asking once here beats a prompt per photo later.
      const handle = await window.showDirectoryPicker({ mode: "readwrite" });
      const found = [];
      for await (const [name, child] of handle.entries()) {
        if (child.kind !== "file" || !isSupportedImage(name)) continue;
        // A second run over the same folder sees the copies from the first
        // one. Listing them is right — the realtor may have renamed something
        // — but pre-ticking them would quietly watermark them twice.
        const already = looksWatermarked(name);
        found.push({
          name: name,
          handle: child,
          selected: !already,
          already: already,
          status: "",
        });
      }
      found.sort(function (a, b) {
        return a.name.localeCompare(b.name, "uk");
      });

      setDirHandle(handle);
      setDirName(handle.name);
      setEntries(found);
      setDoneCount(0);
      setPreview(null);
      setActiveName("");
      if (found.length === 0) {
        setNotice({
          severity: "info",
          text: "У цій папці немає зображень (.jpg, .jpeg, .png, .webp).",
        });
      } else {
        const first = found.filter(function (entry) {
          return entry.selected;
        })[0];
        renderPreview(first || found[0]);
      }
    } catch (err) {
      // The picker throws AbortError when the realtor closes it; not an error.
      if (err && err.name !== "AbortError") {
        setNotice({ severity: "error", text: err.message });
      }
    }
  };

  const pickFiles = function (event) {
    const chosen = Array.prototype.slice.call(event.target.files || []);
    const found = chosen
      .filter(function (file) {
        return isSupportedImage(file.name);
      })
      .map(function (file) {
        const already = looksWatermarked(file.name);
        return {
          name: file.name,
          file: file,
          selected: !already,
          already: already,
          status: "",
        };
      });
    setDirHandle(null);
    setDirName("");
    setEntries(found);
    setDoneCount(0);
    setPreview(null);
    if (found.length > 0) renderPreview(found[0]);
  };

  const toggle = function (name) {
    setEntries(function (current) {
      return current.map(function (entry) {
        if (entry.name !== name) return entry;
        return Object.assign({}, entry, { selected: !entry.selected });
      });
    });
  };

  const setAll = function (value) {
    setEntries(function (current) {
      return current.map(function (entry) {
        return Object.assign({}, entry, { selected: value });
      });
    });
  };

  const markStatus = function (name, status, error) {
    setEntries(function (current) {
      return current.map(function (entry) {
        if (entry.name !== name) return entry;
        return Object.assign({}, entry, { status: status, error: error });
      });
    });
  };

  /**
   * Which of these photos are actually in the folder we were given?
   *
   * Overwriting writes by NAME into the granted directory, so if that is not
   * the folder the photos came from every single write fails with
   * "A requested file or directory could not be found" — which says nothing
   * about the real problem. Checking first costs one lookup per photo and buys
   * a message worth reading.
   */
  const checkPresence = async function (directory, list) {
    const missing = [];
    for (let i = 0; i < list.length; i++) {
      try {
        await directory.getFileHandle(list[i].name);
      } catch (err) {
        missing.push(list[i].name);
      }
    }
    return missing;
  };

  const run = async function (destination, toDownloads) {
    // setDirHandle above has not necessarily landed in state yet.
    const target = toDownloads ? null : destination || dirHandle;
    setConfirmOpen(false);

    if (mode === "overwrite" && target) {
      const missing = await checkPresence(target, selected);
      if (missing.length > 0) {
        setNotice({
          severity: "error",
          text:
            "У теці «" +
            (target.name || dirName) +
            "» немає " +
            (missing.length === selected.length
              ? "жодного з вибраних фото"
              : missing.length + " із вибраних фото") +
            ". Перезапис пише файли за їхніми назвами саме в цю теку, тому " +
            "потрібно вибрати ту саму теку, де лежать фото. Якщо це тека " +
            "завантажень — Chrome її не віддає; перекладіть фото у вкладену " +
            "теку і спробуйте ще раз.",
          canDownload: true,
          downloadIsCopy: true,
        });
        return;
      }
    }
    setRunning(true);
    setDoneCount(0);
    setNotice(null);

    let ok = 0;
    let failed = 0;
    const written = [];

    for (let i = 0; i < selected.length; i++) {
      const entry = selected[i];
      try {
        const file = entry.handle ? await entry.handle.getFile() : entry.file;
        const result = await watermarkImage(file, watermark, { name: entry.name });
        // A download lands in the downloads folder under whatever name we give
        // it — it can never replace an original, so it is always a copy.
        const outName =
          mode === "overwrite" && !toDownloads ? entry.name : copyName(entry.name);

        if (target) {
          // create:false when overwriting, so confirming the wrong folder by
          // mistake reports a missing file instead of quietly scattering new
          // ones next to somebody else's photos.
          const out = await target.getFileHandle(outName, {
            create: mode !== "overwrite",
          });
          const writable = await out.createWritable();
          await writable.write(result.blob);
          await writable.close();
        } else {
          // No directory handle: the best a browser can do is hand it over as
          // a download, which lands in the Downloads folder, not beside the
          // original.
          const url = URL.createObjectURL(result.blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = outName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(function () {
            URL.revokeObjectURL(url);
          }, 30000);
        }

        markStatus(entry.name, "ok");
        written.push(outName);
        ok++;
      } catch (err) {
        markStatus(entry.name, "error", err.message);
        failed++;
      }
      setDoneCount(i + 1);
    }

    setRunning(false);
    setSaved({
      folder: toDownloads
        ? ""
        : (target && target.name) || dirName || "",
      toDownloads: !!toDownloads,
      names: written,
    });
    setNotice({
      severity: failed ? "warning" : "success",
      text:
        (mode === "overwrite" && !toDownloads ? "Перезаписано" : "Збережено копій") +
        ": " +
        ok +
        (failed ? ". Не вдалося: " + failed : "") +
        (mode === "overwrite" && !toDownloads
          ? "."
          : target
          ? " — у папці «" + (target.name || dirName) + "»."
          : " — у теці завантажень."),
    });
  };

  /**
   * Both modes need a directory handle. Ask for it here, while the click that
   * started this is still the current user gesture — the picker is refused
   * once an await has let that lapse.
   */
  const ensureDestination = async function () {
    if (dirHandle) return dirHandle;
    const first = entries[0];
    const options = { mode: "readwrite" };
    // Opens the dialog in the folder the photos came from, so confirming it
    // puts the copies beside their originals.
    if (first && first.handle) options.startIn = first.handle;
    const handle = await window.showDirectoryPicker(options);
    setDirHandle(handle);
    setDirName(handle.name);
    return handle;
  };

  /** Shared by the copy button and the overwrite confirmation. */
  const begin = async function () {
    if (!HAS_FS_ACCESS) {
      run();
      return;
    }

    // Both modes go through a directory handle. Asking each picked file for
    // write access one by one looks tempting and cannot work: requestPermission
    // requires transient user activation AND CONSUMES IT, so the second file in
    // the loop always throws. One folder, one grant, every file inside it.
    try {
      const handle = await ensureDestination();
      run(handle);
    } catch (err) {
      setConfirmOpen(false);
      // Chrome does not throw when a folder is on its blocklist — it refuses
      // inside the dialog and the realtor ends up cancelling, which arrives
      // here as AbortError. So both endings get the same explanation, and copy
      // mode gets an escape that needs no folder at all.
      setNotice({
        severity: err && err.name === "AbortError" ? "info" : "error",
        text: blockedFolderHelp(),
        canDownload: mode !== "overwrite",
      });
    }
  };

  const start = function () {
    if (mode === "overwrite") {
      setConfirmOpen(true);
      return;
    }
    begin();
  };

  const progress = selected.length
    ? Math.round((doneCount / selected.length) * 100)
    : 0;

  return (
    <Container maxWidth={false} sx={{ px: { xs: 1, md: 2 }, pb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          mx: 0,
          mt: { xs: 1, md: 2 },
          mb: 1.5,
          p: { xs: 1.5, md: 2 },
          borderRadius: radius.lg,
          border: "1px solid " + tokens.line,
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <BrandingWatermarkOutlined sx={{ color: tokens.blue600 }} />
          <Box sx={{ flexGrow: 1, minWidth: 200 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              Лого на фото
            </Typography>
            <Typography variant="caption" sx={{ color: tokens.ink400 }}>
              Фото обробляються на вашому комп'ютері й нікуди не надсилаються
            </Typography>
          </Box>

          {saved && saved.names.length > 0 && (
            <Tooltip title="Де збережено">
              <IconButton
                aria-label="Де збережено"
                onClick={function () {
                  setSavedOpen(true);
                }}
                sx={{
                  border: "1px solid " + tokens.line,
                  borderRadius: radius.md,
                  color: tokens.blue700,
                }}
              >
                <FolderOutlined />
              </IconButton>
            </Tooltip>
          )}

          {HAS_FS_ACCESS ? (
            <React.Fragment>
              <Button
                variant="contained"
                disableElevation
                startIcon={<ImageOutlined />}
                onClick={pickPhotos}
                disabled={running}
              >
                Вибрати фото
              </Button>
              <Tooltip title="Взяти всі фото з однієї папки">
                <Button
                  variant="outlined"
                  startIcon={<FolderOpenOutlined />}
                  onClick={pickFolder}
                  disabled={running}
                >
                  Папку
                </Button>
              </Tooltip>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <input
                ref={fileInput}
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.webp"
                style={{ display: "none" }}
                onChange={pickFiles}
              />
              <Button
                variant="contained"
                disableElevation
                startIcon={<ImageOutlined />}
                onClick={function () {
                  fileInput.current.click();
                }}
                disabled={running}
              >
                Вибрати фото
              </Button>
            </React.Fragment>
          )}
        </Stack>
      </Paper>

      {watermarkError && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {watermarkError}
        </Alert>
      )}

      {!HAS_FS_ACCESS && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Цей браузер не дозволяє зберігати файли назад у папку. Копії
          потраплять у теку завантажень, а перезапис оригіналів недоступний.
          У Chrome або Edge працюють обидва режими.
        </Alert>
      )}

      {notice && (
        <Alert
          severity={notice.severity}
          sx={{ mb: 1.5 }}
          onClose={function () {
            setNotice(null);
          }}
          action={
            notice.canDownload ? (
              <Button
                size="small"
                onClick={function () {
                  setNotice(null);
                  run(null, true);
                }}
              >
                {notice.downloadIsCopy
                  ? "Завантажити копії"
                  : "Завантажити замість цього"}
              </Button>
            ) : null
          }
        >
          {notice.text}
        </Alert>
      )}

      {entries.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1.1fr)" },
            gap: 2,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: radius.lg,
              border: "1px solid " + tokens.line,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ px: 2, py: 1.5 }}
            >
              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                {dirName ? "Папка «" + dirName + "»" : "Вибрані фото"}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: tokens.ink400, ml: 1 }}
                >
                  {selected.length} з {entries.length}
                </Typography>
              </Typography>
              <Button size="small" onClick={function () { setAll(true); }} disabled={running}>
                Всі
              </Button>
              <Button size="small" onClick={function () { setAll(false); }} disabled={running}>
                Жодного
              </Button>
            </Stack>
            <Divider />
            <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
              {entries.map(function (entry) {
                return (
                  <Stack
                    key={entry.name}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    onClick={function () {
                      if (!running) renderPreview(entry);
                    }}
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      cursor: "pointer",
                      borderBottom: "1px solid " + tokens.line2,
                      backgroundColor:
                        entry.name === activeName ? tokens.blue50 : "transparent",
                      "&:hover": { backgroundColor: tokens.line2 },
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={entry.selected}
                      disabled={running}
                      onClick={function (event) {
                        event.stopPropagation();
                      }}
                      onChange={function () {
                        toggle(entry.name);
                      }}
                    />
                    <Typography variant="body2" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
                      {entry.name}
                    </Typography>
                    {entry.already && !entry.status && (
                      <Tooltip title="Схоже, лого вже накладено">
                        <Chip
                          size="small"
                          label="-wm"
                          sx={{
                            height: 20,
                            backgroundColor: tokens.sand100,
                            color: tokens.sand700,
                          }}
                        />
                      </Tooltip>
                    )}
                    {entry.status === "ok" && (
                      <CheckCircleOutlined sx={{ fontSize: 18, color: tokens.green600 }} />
                    )}
                    {entry.status === "error" && (
                      <Tooltip title={entry.error || ""}>
                        <ErrorOutlineOutlined sx={{ fontSize: 18, color: tokens.red600 }} />
                      </Tooltip>
                    )}
                  </Stack>
                );
              })}
            </Box>
          </Paper>

          <Stack spacing={2}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: radius.lg,
                border: "1px solid " + tokens.line,
                overflow: "hidden",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2, py: 1.5 }}>
                <PhotoSizeSelectLargeOutlined sx={{ fontSize: 19, color: tokens.ink400 }} />
                <Typography variant="subtitle2" sx={{ flexGrow: 1 }} noWrap>
                  {activeName || "Попередній перегляд"}
                </Typography>
                {preview && (
                  <Chip
                    size="small"
                    label={preview.width + " × " + preview.height}
                    sx={{ backgroundColor: tokens.line2, color: tokens.ink500 }}
                  />
                )}
              </Stack>
              <Divider />
              <Box
                sx={{
                  backgroundColor: tokens.line2,
                  minHeight: 240,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 1,
                }}
              >
                {previewBusy && <LinearProgress sx={{ width: "60%" }} />}
                {!previewBusy && preview && (
                  <Box
                    component="img"
                    src={preview.url}
                    alt={activeName}
                    sx={{ maxWidth: "100%", maxHeight: 420, display: "block" }}
                  />
                )}
                {!previewBusy && !preview && (
                  <Typography variant="caption" sx={{ color: tokens.ink400 }}>
                    Виберіть фото зі списку
                  </Typography>
                )}
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                borderRadius: radius.lg,
                border: "1px solid " + tokens.line,
                p: 2,
              }}
            >
              <RadioGroup
                value={mode}
                onChange={function (event) {
                  setMode(event.target.value);
                }}
              >
                <FormControlLabel
                  value="copy"
                  disabled={running}
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2">Зберегти копії</Typography>
                      <Typography variant="caption" sx={{ color: tokens.ink400 }}>
                        {dirName
                          ? "У папку «" + dirName + "»: IMG_01.jpg → IMG_01-wm.jpg"
                          : HAS_FS_ACCESS
                          ? "IMG_01.jpg → IMG_01-wm.jpg. Папку підтвердите один раз"
                          : "IMG_01.jpg → IMG_01-wm.jpg, у теку завантажень"}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="overwrite"
                  disabled={running || !HAS_FS_ACCESS}
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2">Перезаписати оригінали</Typography>
                      <Typography variant="caption" sx={{ color: tokens.ink400 }}>
                        {dirName
                          ? "У папці «" + dirName + "». Початкові фото буде замінено назавжди"
                          : HAS_FS_ACCESS
                          ? "Початкові фото буде замінено назавжди. Папку підтвердите один раз"
                          : "Недоступно в цьому браузері"}
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>

              {HAS_FS_ACCESS && !dirName && (
                <Typography
                  variant="caption"
                  sx={{ display: "block", mt: 1.5, color: tokens.ink400 }}
                >
                  Chrome не дозволяє вибрати саму теку завантажень, «Робочий
                  стіл», «Документи» чи домашню теку — лише вкладені в них.
                </Typography>
              )}

              {running && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress variant="determinate" value={progress} />
                  <Typography variant="caption" sx={{ color: tokens.ink400 }}>
                    {doneCount} з {selected.length}
                  </Typography>
                </Box>
              )}

              <Button
                fullWidth
                variant="contained"
                disableElevation
                startIcon={<SaveAltOutlined />}
                sx={{ mt: 2 }}
                disabled={running || !watermark || selected.length === 0}
                onClick={start}
              >
                {mode === "overwrite"
                  ? "Перезаписати (" + selected.length + ")"
                  : "Зберегти копії (" + selected.length + ")"}
              </Button>
            </Paper>
          </Stack>
        </Box>
      )}

      {/* A page cannot open Explorer — nothing in a browser can. So the next
          best thing: name the folder and list exactly what landed in it. */}
      <Dialog
        open={savedOpen}
        onClose={function () {
          setSavedOpen(false);
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: radius.lg } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <FolderOutlined sx={{ color: tokens.blue600 }} />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" component="div" sx={{ lineHeight: 1.2 }}>
              {saved && saved.toDownloads
                ? "Тека завантажень"
                : saved && saved.folder
                ? "Тека «" + saved.folder + "»"
                : "Збережені файли"}
            </Typography>
            <Typography variant="caption" sx={{ color: tokens.ink400 }}>
              {saved ? saved.names.length + " файл(ів)" : ""}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 2, py: 1 }}>
          {saved &&
            saved.names.map(function (name) {
              return (
                <Stack
                  key={name}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ py: 0.5 }}
                >
                  <ImageOutlined sx={{ fontSize: 17, color: tokens.ink400 }} />
                  <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                    {name}
                  </Typography>
                </Stack>
              );
            })}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={function () {
              setSavedOpen(false);
            }}
          >
            Закрити
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onClose={function () {
          setConfirmOpen(false);
        }}
        onConfirm={begin}
        tone="danger"
        icon={<WarningAmberOutlined />}
        title={"Перезаписати " + selected.length + " фото?"}
        description={
          "Початкові файли у папці «" +
          dirName +
          "» буде замінено остаточно. Цю дію не можна скасувати."
        }
        confirmLabel="Перезаписати"
      />
    </Container>
  );
}
